import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { ExternalLink, Radio, RefreshCw, MapPin } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ease = [0.22, 1, 0.36, 1];

const TOPICS = [
  ["", "All"],
  ["disaster", "Disasters"],
  ["weather", "Extreme weather"],
  ["earthquake", "Earthquakes"],
  ["flood", "Floods"],
  ["storm", "Storms"],
  ["wildfire", "Wildfires"],
];

function timeAgo(iso) {
  if (!iso) return "";
  const mins = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 60000));
  if (mins < 60) return `${mins || 1}m ago`;
  const hours = Math.round(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  return `${Math.round(hours / 24)}d ago`;
}

/** /news — The Disaster Wire: hourly-scanned disaster & weather headlines, stored in our ledger. */
export default function NewsWire() {
  const [articles, setArticles] = useState([]);
  const [lastScan, setLastScan] = useState(null);
  const [topic, setTopic] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    axios
      .get(`${API}/v1/news`, { params: { topic: topic || undefined } })
      .then(({ data }) => {
        setArticles(data.articles || []);
        setLastScan(data.last_scan);
      })
      .catch(() => setArticles([]))
      .finally(() => setLoading(false));
  }, [topic]);

  return (
    <main data-testid="newswire-page" className="pt-40 md:pt-48">
      <section className="max-w-[1200px] mx-auto px-6 md:px-10 pb-12">
        <div className="overline text-[color:var(--ink-soft)] mb-6 flex items-center gap-2">
          <Radio size={13} className="text-[color:var(--rust)]" /> § The Wire
        </div>
        <motion.h1
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1.0, ease }}
          className="font-serif-display leading-[0.95] tracking-[-0.02em]"
          style={{ fontSize: "clamp(44px, 7vw, 110px)" }}
        >
          The disaster wire, <span className="font-italic-serif italic text-[color:var(--ink-soft)]">hourly.</span>
        </motion.h1>
        <p className="mt-6 max-w-2xl text-[17px] leading-[1.6] text-[color:var(--ink-soft)]">
          Every hour we scan the world's news for disasters and extreme weather, and keep the links here —
          a living record you can trace back to its source. Where a place is struck, we keep a page about the place itself.
        </p>
        <div className="mt-4 numeric-index text-[color:var(--ink-soft)]" data-testid="wire-last-scan">
          {lastScan ? `Last scan ${timeAgo(lastScan)} · refreshed every hour` : "First scan warming up…"}
        </div>
      </section>

      <section className="max-w-[1200px] mx-auto px-6 md:px-10 pb-24">
        <div className="flex flex-wrap gap-2 mb-8" data-testid="wire-topics">
          {TOPICS.map(([v, l]) => (
            <button
              key={v}
              onClick={() => setTopic(v)}
              data-testid={`wire-topic-${v || "all"}`}
              className={`px-4 py-2 text-xs font-heritage-caps tracking-[0.12em] border transition-colors ${
                topic === v
                  ? "border-[color:var(--rust)] bg-[color:var(--rust)] text-[color:var(--bone)]"
                  : "border-[color:var(--sepia)]/40 text-[color:var(--sepia)] hover:bg-[color:var(--parchment-2)]"
              }`}
            >
              {l}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="py-16 text-center text-[color:var(--ink-soft)]">
            <RefreshCw size={16} className="inline animate-spin mr-2" /> Reading the wire…
          </div>
        ) : articles.length === 0 ? (
          <div className="border border-dashed border-[color:var(--ink-line-strong)] p-14 text-center" data-testid="wire-empty">
            <p className="font-italic-serif italic text-xl text-[color:var(--ink-soft)]">
              The wire is quiet — the next hourly scan will bring the headlines.
            </p>
          </div>
        ) : (
          <ol className="divide-y divide-[color:var(--sepia)]/15 border-t border-b border-[color:var(--sepia)]/25" data-testid="wire-list">
            {articles.map((a, i) => (
              <motion.li
                key={a.id}
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: Math.min(i, 10) * 0.03, ease }}
                data-testid={`wire-item-${i}`}
              >
                <div className="py-4 flex items-baseline gap-4">
                  <span className="numeric-index text-[color:var(--sepia)] shrink-0 w-8">{String(i + 1).padStart(2, "0")}</span>
                  <div className="min-w-0 flex-1">
                    <a
                      href={a.link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="font-serif-display text-lg leading-snug hover:text-[color:var(--rust)] transition-colors inline-flex items-baseline gap-1.5"
                    >
                      {a.title} <ExternalLink size={12} className="shrink-0 text-[color:var(--sepia)]/60" />
                    </a>
                    <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 numeric-index text-[color:var(--ink-soft)]">
                      <span>{a.source}</span>
                      <span>{timeAgo(a.published || a.created_at)}</span>
                      {a.place_slug && (
                        <Link
                          to={`/places/${a.place_slug}`}
                          data-testid={`wire-place-${i}`}
                          className="inline-flex items-center gap-1 link-underline text-[color:var(--rust)]"
                        >
                          <MapPin size={10} /> More about {a.location}
                        </Link>
                      )}
                      {a.disaster_id && (
                        <Link to={`/disasters/${a.disaster_id}`} className="link-underline text-[color:var(--sepia)]">
                          disaster record →
                        </Link>
                      )}
                    </div>
                  </div>
                </div>
              </motion.li>
            ))}
          </ol>
        )}
        <p className="mt-4 numeric-index text-[color:var(--ink-soft)]">
          Headlines via Google News · we store the link, never the article — reading happens at the publisher.
        </p>
      </section>
    </main>
  );
}
